import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Pencil, Trash2, Calendar } from 'lucide-react';
import { API_BASE_URL as API_URL } from '../utils/api';
import { getLocalized } from '../utils/postLocalization';

const PostCard = ({ post, linkTo, onEdit, onDelete }) => {
  const { i18n, t } = useTranslation();
  const lang = i18n.language;
  const title = getLocalized(post.title, lang);
  const bodySnippet = getLocalized(post.body, lang);
  const authorName = post.createdBy?.fullName || t('common.administrator');
  const hasImage = post.images && post.images.length > 0;
  const coverSrc = hasImage ? `${API_URL}${post.images[0]}` : '/no_image.webp';

  const dateStr = new Date(post.createdAt)
    .toISOString()
    .slice(0, 10)
    .split('-')
    .reverse()
    .join('.');

  return (
    <Link
      to={linkTo}
      className="block bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
    >
      <div className="relative aspect-video bg-gray-100">
        <img
          src={coverSrc}
          alt={title}
          className="w-full h-full object-cover"
          onError={(e) => {
            if (e.currentTarget.dataset.fallback !== '1') {
              e.currentTarget.dataset.fallback = '1';
              e.currentTarget.src = '/no_image.webp';
            }
          }}
        />

        {onEdit && (
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onEdit(post);
            }}
            className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center bg-white/90 backdrop-blur text-gray-700 rounded-lg hover:bg-white"
            title={t('installationInstructions.editButton')}
          >
            <Pencil className="w-4 h-4" />
          </button>
        )}

        {onDelete && (
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onDelete(post);
            }}
            className="absolute top-3 right-14 w-8 h-8 flex items-center justify-center bg-white/90 backdrop-blur text-red-700 rounded-lg hover:bg-red-50"
            title={t('installationInstructions.deleteButton')}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}

        <div className="absolute bottom-3 right-3 flex flex-col items-end gap-1 bg-black/50 backdrop-blur px-2 py-1 rounded text-xs text-white">
          <span>{t('installationInstructions.card.author', { name: authorName })}</span>
          <span className="flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            {dateStr}
          </span>
        </div>
      </div>

      <div className="p-4">
        <h3 className="font-semibold text-gray-900 truncate">{title}</h3>
        <p className="text-sm text-gray-600 mt-1 line-clamp-3 whitespace-pre-wrap">
          {bodySnippet}
        </p>
        <div className="mt-3 text-right">
          <span className="text-sm text-primary-600 font-medium">
            {t('installationInstructions.card.readMore')}
          </span>
        </div>
      </div>
    </Link>
  );
};

export default PostCard;
